import api from './api'
import { AlteracaoGestao } from '@/types/campanha'
import {
  AtribuicaoNivelLinha,
  AtribuicaoNivelRequest,
  ConfiguracaoNivel,
  FiltroAtribuicoes,
  HistoricoNivel,
  MeuNivelResponse,
  NivelConsultor,
  NivelConsultorRequest,
  NivelResumido,
  PaginaAtribuicoes,
} from '@/types/nivelConsultor'

/** `NivelConsultorController` + `AtribuicaoNivelController` (AN-10 §8, oportunidades). */
const BASE_URL = '/api/v1/niveis-consultor'

/**
 * O backend responde o `PageResponse` do originei-common (`pageNumber`/`pageSize`, base 0);
 * aceita também lista pura (backend sem paginação) para não quebrar a tela.
 */
const comoPagina = (r: PaginaAtribuicoes | AtribuicaoNivelLinha[]): PaginaAtribuicoes =>
  Array.isArray(r)
    ? {
        content: r,
        pageNumber: 0,
        pageSize: r.length,
        totalElements: r.length,
        totalPages: 1,
        first: true,
        last: true,
        empty: r.length === 0,
      }
    : r

export const nivelConsultorService = {
  /** CONSULTOR_OR_ABOVE; escopo = `sub` do token (não há como pedir o de outro). */
  meu: async (): Promise<NivelResumido | null> => {
    const r = await api.get<MeuNivelResponse | null>(`${BASE_URL}/meu`)
    return r?.nivel ?? null
  },

  /** ADMIN: `{criteriosAutomacaoHabilitado}` (feature flag; padrão `false`). */
  configuracao: async (): Promise<ConfiguracaoNivel> => {
    return api.get<ConfiguracaoNivel>(`${BASE_URL}/configuracao`)
  },

  /** ADMIN: catálogo completo (inclui inativos), com a contagem de consultores. */
  listar: async (incluirInativos = true): Promise<NivelConsultor[]> => {
    return api.get<NivelConsultor[]>(`${BASE_URL}?incluirInativos=${incluirInativos}`)
  },

  /** ADMIN. 409 NIVEL_NOME_DUPLICADO / NIVEL_ORDEM_EM_USO · 400 NIVEL_COR_INVALIDA. */
  criar: async (req: NivelConsultorRequest): Promise<NivelConsultor> => {
    return api.post<NivelConsultor>(BASE_URL, req)
  },

  /** ADMIN, com `versao` (409 NIVEL_ALTERADO_POR_OUTRO). */
  atualizar: async (id: string, req: NivelConsultorRequest): Promise<NivelConsultor> => {
    return api.put<NivelConsultor>(`${BASE_URL}/${id}`, req)
  },

  /** ADMIN: lista COMPLETA de ids na nova ordem (400 NIVEL_ORDEM_INCOMPLETA). */
  reordenar: async (ids: string[]): Promise<NivelConsultor[]> => {
    return api.put<NivelConsultor[]>(`${BASE_URL}/ordem`, { ids })
  },

  /** ADMIN: ativar/inativar (sem exclusão). */
  alterarStatus: async (id: string, ativo: boolean): Promise<NivelConsultor> => {
    return api.patch<NivelConsultor>(`${BASE_URL}/${id}/status`, { ativo })
  },

  /** ADMIN: trilha do catálogo (`entidade = 'NIVEL'`), do mais antigo para o mais recente (ordem do backend). */
  alteracoes: async (): Promise<AlteracaoGestao[]> => {
    return api.get<AlteracaoGestao[]>(`${BASE_URL}/alteracoes`)
  },

  /** ADMIN (todos os consultores ativos) e GERENTE+GESTOR_EQUIPE (só membros da equipe). */
  atribuicoes: async (f: FiltroAtribuicoes = {}): Promise<PaginaAtribuicoes> => {
    const params = new URLSearchParams()
    if (f.busca?.trim()) params.append('busca', f.busca.trim())
    if (f.nivelId) params.append('nivelId', f.nivelId)
    if (f.semNivel) params.append('semNivel', 'true')
    if (f.emNivelInativo) params.append('emNivelInativo', 'true')
    if (f.page !== undefined) params.append('page', String(f.page))
    if (f.size !== undefined) params.append('size', String(f.size))
    const q = params.toString()
    const r = await api.get<PaginaAtribuicoes | AtribuicaoNivelLinha[]>(
      `${BASE_URL}/atribuicoes${q ? `?${q}` : ''}`
    )
    return comoPagina(r)
  },

  /**
   * ADMIN: atribui, troca ou remove (`nivelId: null`) — justificativa 10–500 sempre.
   * 400 NIVEL_JUSTIFICATIVA_OBRIGATORIA · 422 CONSULTOR_INVALIDO / NIVEL_INATIVO / NIVEL_SEM_MUDANCA ·
   * 404 NIVEL_NAO_ENCONTRADO · 409 NIVEL_ATRIBUICAO_CONCORRENTE.
   */
  atribuir: async (consultorId: string, req: AtribuicaoNivelRequest): Promise<void> => {
    await api.put(`${BASE_URL}/atribuicoes/${consultorId}`, req)
  },

  /** ADMIN: histórico append-only com justificativa (o gerente não vê), do mais recente para o mais antigo. */
  historico: async (consultorId: string): Promise<HistoricoNivel[]> => {
    return api.get<HistoricoNivel[]>(`${BASE_URL}/atribuicoes/${consultorId}/historico`)
  },
}

export default nivelConsultorService
